import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, Trash2, Save, Eye, Ruler, VolumeX } from 'lucide-react';
import { apiFetch, getStoredToken } from '../App';
import { Button } from './ui/button';
import { Label } from './ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Small wrapper that mirrors AdminPanel's `nativeFetchWithAuth` — we use
// it for the multipart upload + JSON PATCH/DELETE calls where `apiFetch`'s
// automatic throw-on-non-OK gets in the way.
const authFetch = (url, options = {}) => {
  const token = getStoredToken();
  return fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
};

/*
 * AdminCoachTab
 *
 * One screen — no rooms, no batches — for managing the 12 Coach button
 * explainer clips.
 *
 * Top:    Global display-width sliders (desktop / mobile) so the admin
 *         can dial in the player size that feels right without editing
 *         individual clips.
 * Middle: Upload button + modal.
 * Bottom: Clips grid grouped by target_button_id so it's obvious which
 *         buttons still need more variety (5-per-target is the target).
 */
export default function AdminCoachTab() {
  const [targets, setTargets] = useState([]);
  const [clips, setClips] = useState([]);
  const [settings, setSettings] = useState({ display_width_desktop: 400, display_width_mobile: 280 });
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  // Track whether we've already auto-run backfill this session — prevents
  // an infinite loop if some clips are un-probable (e.g. HP is down).
  const autoBackfillRan = useRef(false);
  // Track the currently-playing clip's <video> element across ALL cards so
  // starting a new clip's hover-preview pauses any other clip that's still
  // playing. Prevents overlapping audio when the admin sweeps across
  // multiple cards.
  const activeVideoRef = useRef(null);
  const stopOtherVideos = useCallback((current) => {
    const prev = activeVideoRef.current;
    if (prev && prev !== current && !prev.paused) {
      try {
        prev.pause();
        prev.muted = true;
        prev.currentTime = 0;
      } catch { /* ignore */ }
    }
    activeVideoRef.current = current;
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [clipsRes, settingsRes] = await Promise.all([
        apiFetch('/admin/coach/clips'),
        apiFetch('/admin/coach/settings'),
      ]);
      setClips(clipsRes.data.clips || []);
      setTargets(clipsRes.data.targets || []);
      setSettings(settingsRes.data || settings);
    } catch (e) {
      toast.error('Failed to load Coach data');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { reload(); }, [reload]);

  // Auto-backfill missing dimensions once per mount. Runs silently in the
  // background — no toast unless it actually updated something. Keeps the
  // grid honest without the admin needing to remember the "Backfill Sizes"
  // button.
  useEffect(() => {
    if (loading || autoBackfillRan.current || clips.length === 0) return;
    const missing = clips.filter((c) => !c.width || !c.height);
    if (missing.length === 0) return;
    autoBackfillRan.current = true;
    (async () => {
      try {
        const res = await authFetch(`${API}/admin/coach/backfill-dimensions`, { method: 'POST' });
        if (!res.ok) return;
        const data = await res.json();
        if (data.updated > 0) {
          toast.success(`Auto-backfilled dimensions for ${data.updated} existing clip${data.updated === 1 ? '' : 's'}`);
          await reload();
        }
      } catch { /* silent — user can hit the manual button if it matters */ }
    })();
  }, [clips, loading, reload]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const fd = new FormData();
      fd.append('display_width_desktop', String(settings.display_width_desktop));
      fd.append('display_width_mobile', String(settings.display_width_mobile));
      const res = await authFetch(`${API}/admin/coach/settings`, { method: 'PUT', body: fd });
      if (!res.ok) throw new Error('save failed');
      toast.success('Coach display settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const patchClip = async (clip_id, updates) => {
    try {
      const res = await authFetch(`${API}/admin/coach/clip/${clip_id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error('patch failed');
      setClips((prev) => prev.map((c) => c.clip_id === clip_id ? { ...c, ...updates } : c));
    } catch {
      toast.error('Update failed');
    }
  };

  const deleteClip = async (clip_id, filename) => {
    if (!window.confirm(`Delete ${filename}? This can't be undone.`)) return;
    try {
      const res = await authFetch(`${API}/admin/coach/clip/${clip_id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      setClips((prev) => prev.filter((c) => c.clip_id !== clip_id));
      toast.success('Clip deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const backfillDims = async () => {
    setBackfilling(true);
    try {
      const res = await authFetch(`${API}/admin/coach/backfill-dimensions`, { method: 'POST' });
      if (!res.ok) throw new Error('backfill failed');
      const data = await res.json();
      toast.success(`Backfill: ${data.updated} updated · ${data.failed} failed · ${data.scanned} scanned`);
      await reload();
    } catch {
      toast.error('Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };

  // Group clips by target for the display below.
  const clipsByTarget = clips.reduce((acc, c) => {
    (acc[c.target_button_id] ??= []).push(c);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="glass-card p-5">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Eye className="h-5 w-5 text-primary" /> Coach Studio
            </h3>
            <p className="text-zinc-400 text-sm">Short character explainers attached to specific UI buttons. Upload 3–5 clips per target for variety — the player picks one at random.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={backfillDims} disabled={backfilling} variant="outline" size="sm" data-testid="coach-backfill-dims">
              <Ruler className="h-4 w-4 mr-2" /> {backfilling ? 'Scanning…' : 'Backfill Sizes'}
            </Button>
            <Button onClick={() => setUploadOpen(true)} className="btn-primary" data-testid="coach-upload-open">
              <Upload className="h-4 w-4 mr-2" /> Upload Clip
            </Button>
          </div>
        </div>
      </div>

      {/* Global display-size sliders */}
      <div className="glass-card p-5" data-testid="coach-settings">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-semibold text-white uppercase tracking-wide">Display size</div>
          <Button onClick={saveSettings} disabled={savingSettings} size="sm" className="btn-primary">
            <Save className="h-3.5 w-3.5 mr-1" /> {savingSettings ? 'Saving…' : 'Save'}
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <SizeSlider
            testid="coach-slider-desktop"
            label="Desktop width"
            min={240} max={640} step={20}
            value={settings.display_width_desktop}
            onChange={(v) => setSettings((s) => ({ ...s, display_width_desktop: v }))}
          />
          <SizeSlider
            testid="coach-slider-mobile"
            label="Mobile width"
            min={200} max={400} step={20}
            value={settings.display_width_mobile}
            onChange={(v) => setSettings((s) => ({ ...s, display_width_mobile: v }))}
          />
        </div>
      </div>

      {/* Grid of targets */}
      {loading ? (
        <div className="glass-card p-8 text-center text-zinc-500 text-sm">Loading…</div>
      ) : (
        <div className="space-y-3">
          {targets.map((t) => {
            const list = clipsByTarget[t.id] || [];
            return (
              <div key={t.id} className="glass-card p-4" data-testid={`coach-target-${t.id}`}>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <div className="text-sm text-white font-semibold">
                      <span className="text-zinc-500 mr-2">#{t.help_no}</span>{t.label}
                    </div>
                    <div className="text-[11px] text-zinc-500">
                      <code>{t.id}</code> · {t.page}
                    </div>
                  </div>
                  <div className={`text-xs font-semibold px-2 py-1 rounded ${
                    list.length === 0 ? 'bg-red-500/20 text-red-300' :
                    list.length < 3 ? 'bg-orange-500/20 text-orange-300' :
                    'bg-green-500/20 text-green-300'
                  }`}>
                    {list.length} clip{list.length === 1 ? '' : 's'}
                  </div>
                </div>
                {list.length === 0 ? (
                  <div className="text-xs text-zinc-500 italic">No clips yet.</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {list.map((c) => (
                      <CoachClipCard
                        key={c.clip_id}
                        clip={c}
                        onPatch={patchClip}
                        onDelete={() => deleteClip(c.clip_id, c.filename)}
                        stopOtherVideos={stopOtherVideos}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {uploadOpen && (
        <UploadModal
          targets={targets}
          currentMaxDim={settings.display_width_desktop}
          onClose={() => setUploadOpen(false)}
          onUploaded={() => { setUploadOpen(false); reload(); }}
        />
      )}
    </div>
  );
}

function SizeSlider({ label, min, max, step, value, onChange, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-white text-sm">{label}</Label>
        <span className="text-primary font-mono text-sm">{value}px</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="w-full accent-primary" data-testid={testid}
      />
      <div className="flex justify-between text-[10px] text-zinc-500 mt-1"><span>{min}</span><span>{max}</span></div>
    </div>
  );
}

function CoachClipCard({ clip, onPatch, onDelete, stopOtherVideos }) {
  const [weight, setWeight] = useState(clip.weight || 1);
  const [transcript, setTranscript] = useState(clip.transcript || '');
  const [showTranscript, setShowTranscript] = useState(false);
  const [forceMuted, setForceMuted] = useState(false);
  const videoRef = useRef(null);

  const handleMouseEnter = () => {
    const v = videoRef.current;
    if (!v) return;
    // Pause any OTHER clip that's still playing so we never have
    // overlapping audio. Registers this video as the active one.
    if (stopOtherVideos) stopOtherVideos(v);
    // Kick a load in case preload didn't fire yet — otherwise play() can
    // race the network and the browser falls back to muted before the
    // buffer is warm.
    try { if (v.readyState < 2) v.load(); } catch { /* no-op */ }
    try {
      v.currentTime = 0;
      v.muted = false;
      v.volume = 1;
      const p = v.play();
      if (p && typeof p.catch === 'function') {
        p.then(() => setForceMuted(false)).catch(() => {
          // Browser blocked unmuted autoplay — fall back to muted so the
          // admin still sees motion. Surface a speaker-off badge so it's
          // obvious the audio is being throttled by the browser.
          v.muted = true;
          setForceMuted(true);
          v.play().catch(() => {});
        });
      }
    } catch { /* no-op */ }
  };
  const handleMouseLeave = () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      v.pause();
      v.muted = true;
      v.currentTime = 0;
    } catch { /* no-op */ }
    setForceMuted(false);
  };
  const forceUnmute = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    v.volume = 1;
    setForceMuted(false);
    v.play().catch(() => {});
  };

  const dimLabel = (clip.width && clip.height) ? `${clip.width}×${clip.height}` : null;
  const sizeLabel = clip.size_bytes ? `${(clip.size_bytes/1024).toFixed(0)}KB` : null;

  return (
    <div className="bg-black/30 border border-white/10 rounded-lg p-2" data-testid={`coach-clip-${clip.clip_id}`}>
      <div
        className="aspect-square bg-black rounded mb-2 overflow-hidden relative group"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <video
          ref={videoRef}
          src={clip.video_url}
          className="w-full h-full object-cover"
          preload="auto"
          playsInline
          controls={false}
          data-testid={`coach-clip-video-${clip.clip_id}`}
        />
        {forceMuted && (
          <button
            type="button"
            onClick={forceUnmute}
            className="absolute top-1 right-1 z-10 bg-black/70 border border-primary/40 text-primary rounded p-1"
            title="Browser muted the preview — click to unmute"
            data-testid={`coach-clip-unmute-${clip.clip_id}`}
          >
            <VolumeX className="h-3 w-3" />
          </button>
        )}
        {/* Subtle "hover to preview" hint that fades on hover */}
        <div className="absolute inset-x-0 bottom-0 px-2 py-1 bg-gradient-to-t from-black/70 to-transparent text-[10px] text-white/70 pointer-events-none opacity-100 group-hover:opacity-0 transition-opacity">
          hover to preview
        </div>
      </div>
      <div className="flex items-center gap-1.5 min-w-0" data-testid={`coach-clip-meta-${clip.clip_id}`}>
        <span className="text-xs text-white truncate font-mono flex-1" title={clip.filename}>{clip.filename}</span>
        {dimLabel && (
          <span
            className="text-[10px] text-primary font-mono bg-primary/10 border border-primary/20 rounded px-1 py-0.5 shrink-0"
            data-testid={`coach-clip-dims-${clip.clip_id}`}
            title={sizeLabel ? `${dimLabel} · ${sizeLabel}` : dimLabel}
          >
            {dimLabel}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <label className="text-[10px] text-zinc-500">weight</label>
        <input
          type="number" min={1} max={10} value={weight}
          onChange={(e) => setWeight(parseInt(e.target.value || '1', 10))}
          onBlur={() => weight !== (clip.weight || 1) && onPatch(clip.clip_id, { weight })}
          className="w-14 px-1 py-0.5 bg-black/40 border border-white/10 rounded text-white text-xs"
        />
        <button
          onClick={() => onPatch(clip.clip_id, { enabled: !clip.enabled })}
          className={`text-[10px] px-1.5 py-0.5 rounded ${clip.enabled ? 'bg-green-500/20 text-green-300' : 'bg-zinc-700 text-zinc-400'}`}
          title="Toggle enabled"
        >
          {clip.enabled ? 'enabled' : 'disabled'}
        </button>
        <button onClick={onDelete} className="ml-auto text-zinc-500 hover:text-red-400" title="Delete">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <button
        onClick={() => setShowTranscript((s) => !s)}
        className="text-[10px] text-zinc-500 hover:text-white mt-1"
      >
        {showTranscript ? '− transcript' : '+ transcript'}
      </button>
      {showTranscript && (
        <div className="mt-1">
          <textarea
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            onBlur={() => transcript !== (clip.transcript || '') && onPatch(clip.clip_id, { transcript })}
            rows={3}
            placeholder="Optional soft-subtitle overlay text…"
            className="w-full text-[11px] p-1 bg-black/40 border border-white/10 rounded text-white"
          />
        </div>
      )}
    </div>
  );
}

function UploadModal({ targets, currentMaxDim, onClose, onUploaded }) {
  const [targetId, setTargetId] = useState(targets[0]?.id || '');
  const [file, setFile] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [weight, setWeight] = useState(1);
  const [audio, setAudio] = useState(true);
  const [downscale, setDownscale] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) { toast.error('Choose a clip file'); return; }
    if (!targetId) { toast.error('Select a target button'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('target_button_id', targetId);
      fd.append('transcript', transcript);
      fd.append('weight', String(weight));
      fd.append('audio_enabled', String(audio));
      // Pass 0 when the admin opts out of auto-downscale so the backend
      // uploads bytes as-is (useful for high-res "hero" clips).
      fd.append('target_max_dim', String(downscale ? (currentMaxDim || 0) : 0));
      fd.append('file', file);
      const res = await authFetch(`${API}/admin/coach/upload`, { method: 'POST', body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'upload failed');
      }
      const data = await res.json().catch(() => ({}));
      const info = data?.downscale;
      if (info?.applied) {
        toast.success(`Uploaded · downscaled ${info.source_dims} → ${info.output_dims}`);
      } else {
        toast.success('Clip uploaded');
      }
      onUploaded();
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9990] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-900 border border-white/10 rounded-xl p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-white mb-3">Upload Coach Clip</h3>
        <div className="space-y-3">
          <div>
            <Label className="text-white text-sm">Target button</Label>
            <select
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              className="w-full mt-1 px-2 py-1.5 bg-black/40 border border-white/10 rounded text-white text-sm"
              data-testid="coach-upload-target"
            >
              {targets.map((t) => (
                <option key={t.id} value={t.id}>
                  #{t.help_no} — {t.label} ({t.page})
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-white text-sm">Video file (.mp4/.webm, ≤25MB)</Label>
            <input
              type="file"
              accept="video/mp4,video/webm,.mp4,.webm,.mov"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1 text-white text-sm block w-full"
              data-testid="coach-upload-file"
            />
            {file && <div className="text-xs text-zinc-500 mt-1">{file.name} · {(file.size/1024/1024).toFixed(1)} MB</div>}
          </div>
          <div>
            <Label className="text-white text-sm">Transcript (optional)</Label>
            <textarea
              rows={3} value={transcript} onChange={(e) => setTranscript(e.target.value)}
              placeholder="Shown as soft-subtitle overlay under the video."
              className="w-full mt-1 px-2 py-1.5 bg-black/40 border border-white/10 rounded text-white text-sm"
            />
          </div>
          <div className="flex items-center gap-3">
            <label className="text-white text-sm flex items-center gap-1">
              Weight
              <input
                type="number" min={1} max={10} value={weight}
                onChange={(e) => setWeight(parseInt(e.target.value || '1', 10))}
                className="ml-1 w-16 px-1.5 py-0.5 bg-black/40 border border-white/10 rounded text-white text-sm"
              />
            </label>
            <label className="text-white text-sm flex items-center gap-1">
              <input type="checkbox" checked={audio} onChange={(e) => setAudio(e.target.checked)} />
              Audio enabled
            </label>
          </div>
          <div className="bg-black/30 border border-white/10 rounded p-2">
            <label className="text-white text-sm flex items-center gap-2" data-testid="coach-upload-downscale-toggle">
              <input
                type="checkbox"
                checked={downscale}
                onChange={(e) => setDownscale(e.target.checked)}
              />
              Auto-downscale to <span className="text-primary font-mono">{currentMaxDim || '—'}px</span> (longest side)
            </label>
            <div className="text-[11px] text-zinc-500 mt-1">
              Re-encodes on our server via ffmpeg (h.264 MP4). No 3rd-party cost.
              Skipped automatically if the source is already ≤ target.
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button onClick={onClose} variant="outline" disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !file} className="btn-primary" data-testid="coach-upload-submit">
            {busy ? 'Uploading…' : (<><Upload className="h-4 w-4 mr-2" />Upload</>)}
          </Button>
        </div>
      </div>
    </div>
  );
}
